import { useState, useEffect, useRef, useMemo } from 'react'
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  IconButton,
  Collapse,
  Paper,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material'
import {
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material'
import { useAdventureEditorStore, selectCategorySettings } from '../../../stores/adventureEditorStore'
import { adventuresApi } from '@api/endpoints/adventures'
import { settingsApi } from '@api/endpoints/settings'
import { MeasurementUnit } from '@/types/settings'
import { ConditionConfigPanel } from '../../workflow-designer/conditions'
import {
  WorkflowConditionType,
  getConditionTypeLabel,
} from '../../../types/workflow.types'
import {
  getCriteriaTypeIcon,
  CRITERIA_CATEGORIES,
  getAvailableCriteriaTypes,
  isGeoCacheCategory,
  getOperatorDisplay,
  isSubscriptionRequired,
  INPUT_CRITERIA_TYPES,
  type CriteriaItem,
  type CriteriaLogicGroup,
} from '../../../types/adventure-editor.types'
import { LogicGroupHeader } from './LogicGroupHeader'
import { useCollaborativeAdventureEditor } from '@hooks/useCollaborativeAdventureEditor'
import { usePublishedPermissions } from '../../../hooks/usePublishedPermissions'

interface CriteriaPanelProps {
  type: 'display' | 'join'
  title: string
  description: string
  icon: React.ReactNode
  adventureId?: string
  headerAction?: React.ReactNode
}

export function CriteriaPanel({
  type,
  title,
  description,
  icon,
  adventureId,
  headerAction,
}: CriteriaPanelProps) {
  // Published permissions - only admins/moderators can edit criteria on published adventures
  const { isPublished, canEditCriteria } = usePublishedPermissions()

  // Fetch user's measurement unit preference
  const [measurementUnit, setMeasurementUnit] = useState<MeasurementUnit>(MeasurementUnit.Imperial)

  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const prefs = await settingsApi.getUserPreferences()
        setMeasurementUnit(prefs.measurementUnit ?? MeasurementUnit.Imperial)
      } catch (error) {
        console.error('Error fetching user preferences:', error)
      }
    }
    fetchPreferences()
  }, [])

  // Get state and actions from store
  const items = useAdventureEditorStore((state) =>
    type === 'display' ? state.displayCriteriaItems : state.joinCriteriaItems
  )
  const logicGroups = useAdventureEditorStore((state) =>
    type === 'display' ? state.displayCriteriaLogicGroups : state.joinCriteriaLogicGroups
  )
  const setCriteriaItems = useAdventureEditorStore((state) => state.setCriteriaItems)
  const mainCategory = useAdventureEditorStore((state) => state.generalInfo.mainCategory)
  const categorySettings = useAdventureEditorStore(selectCategorySettings)
  const adventure = useAdventureEditorStore((state) => state.adventure)
  const geocacheCode = adventure?.geocacheCode || ''

  // Get available criteria types based on adventure category and category settings
  const isGeoCache = isGeoCacheCategory(mainCategory)

  // Get allowed criteria from category settings (null/empty = all allowed)
  const allowedCriteriaFromCategory = useMemo(() => {
    if (!categorySettings) return null
    return type === 'display'
      ? categorySettings.allowedDisplayCriteria
      : categorySettings.allowedJoinCriteria
  }, [categorySettings, type])

  // Compute available criteria types - filter by category's allowed list if specified
  const availableCriteriaTypes = useMemo(() => {
    // Start with base types from adventure category (handles GeoCache restrictions, etc.)
    const baseTypes = getAvailableCriteriaTypes(mainCategory, type)

    // If category settings specify allowed criteria, use those
    if (allowedCriteriaFromCategory && allowedCriteriaFromCategory.length > 0) {
      // Convert to Set for faster lookups (also handles number/enum comparison)
      const allowedSet = new Set(allowedCriteriaFromCategory)
      const inputTypesSet = new Set(INPUT_CRITERIA_TYPES.map(t => t as number))

      // Check if category settings include any input types (QRCode, NumericInput, etc.)
      const hasInputTypes = allowedCriteriaFromCategory.some((ct) => inputTypesSet.has(ct))

      if (hasInputTypes) {
        // When category settings include input types, use allowed list directly
        // (input types are explicitly enabled by the category configuration)
        const allValidTypes = new Set([...baseTypes.map(t => t as number), ...INPUT_CRITERIA_TYPES.map(t => t as number)])
        return allowedCriteriaFromCategory.filter((ct) => allValidTypes.has(ct)) as WorkflowConditionType[]
      }

      // No input types - filter base types by allowed list
      return baseTypes.filter((ct) => allowedSet.has(ct as number))
    }

    // null/empty allowed list = all base types allowed (no input types by default)
    return baseTypes
  }, [mainCategory, type, allowedCriteriaFromCategory])

  // Use collaborative hook for actions - syncs changes with other editors
  const {
    addCriteriaItem: collaborativeAddCriteriaItem,
    updateCriteriaItem: collaborativeUpdateCriteriaItem,
    removeCriteriaItem: collaborativeRemoveCriteriaItem,
    addCriteriaLogicGroup: collaborativeAddCriteriaLogicGroup,
    updateCriteriaLogicGroup: collaborativeUpdateCriteriaLogicGroup,
    removeCriteriaLogicGroup: collaborativeRemoveCriteriaLogicGroup,
  } = useCollaborativeAdventureEditor()

  // Wrap with type for convenience
  const addCriteriaItem = (conditionType: WorkflowConditionType, logicGroupId: string) =>
    collaborativeAddCriteriaItem(type, conditionType, logicGroupId)
  const updateCriteriaItem = (itemId: string, config: Record<string, unknown>) =>
    collaborativeUpdateCriteriaItem(type, itemId, config)
  const removeCriteriaItem = (itemId: string) =>
    collaborativeRemoveCriteriaItem(type, itemId)
  const addCriteriaLogicGroup = (operator: 'AND' | 'OR' = 'AND') =>
    collaborativeAddCriteriaLogicGroup(type, operator)
  const updateCriteriaLogicGroup = (groupId: string, operator: 'AND' | 'OR') =>
    collaborativeUpdateCriteriaLogicGroup(type, groupId, operator)
  const removeCriteriaLogicGroup = (groupId: string) =>
    collaborativeRemoveCriteriaLogicGroup(type, groupId)

  // UI state
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null)
  const [addMenuGroupId, setAddMenuGroupId] = useState<string | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const [isCreatingInitialGroup, setIsCreatingInitialGroup] = useState(false)

  // Track if we've already attempted to create the initial group
  const hasAttemptedInitialGroupRef = useRef(false)
  const hasFetchedRef = useRef(false)

  // Fetch criteria from API on mount
  useEffect(() => {
    const fetchCriteria = async () => {
      if (!adventureId || hasFetchedRef.current) {
        setIsLoading(false)
        return
      }

      hasFetchedRef.current = true

      try {
        const apiCall = type === 'display'
          ? adventuresApi.getDisplayCriteria
          : adventuresApi.getJoinCriteria

        const groups = await apiCall(adventureId)

        // Transform nested response to flat items + groups
        const criteriaItems: CriteriaItem[] = []
        const criteriaLogicGroups: CriteriaLogicGroup[] = []

        for (const group of groups) {
          criteriaLogicGroups.push({
            id: group.id,
            isAnd: group.isAnd,
            orderIndex: group.orderIndex,
          })

          for (const criterion of group.criteria) {
            criteriaItems.push({
              id: criterion.id,
              conditionType: criterion.conditionType as WorkflowConditionType,
              config: criterion.config ?? {},
              logicGroupId: group.id,
              order: criterion.priority ?? 0,
            })
          }
        }

        // Update store with fetched data
        setCriteriaItems(type, criteriaItems, criteriaLogicGroups)
      } catch (error) {
        console.error(`[CriteriaPanel] Failed to fetch ${type} criteria:`, error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchCriteria()
  }, [adventureId, type, setCriteriaItems])

  // Auto-create a logic group if none exist (async API call)
  // Only run after loading is complete
  useEffect(() => {
    const createInitialGroup = async () => {
      if (!isLoading && logicGroups.length === 0 && !hasAttemptedInitialGroupRef.current && !isCreatingInitialGroup) {
        hasAttemptedInitialGroupRef.current = true
        setIsCreatingInitialGroup(true)
        try {
          await addCriteriaLogicGroup('AND')
        } finally {
          setIsCreatingInitialGroup(false)
        }
      }
    }
    createInitialGroup()
  }, [isLoading, logicGroups.length, addCriteriaLogicGroup, isCreatingInitialGroup])

  // Get items for a specific group
  const getItemsForGroup = (groupId: string) => {
    return items
      .filter((item) => item.logicGroupId === groupId)
      .sort((a, b) => a.order - b.order)
  }

  // Toggle item expansion
  const toggleItemExpanded = (itemId: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev)
      if (next.has(itemId)) {
        next.delete(itemId)
      } else {
        next.add(itemId)
      }
      return next
    })
  }

  // Handle add menu open
  const handleAddMenuOpen = (event: React.MouseEvent<HTMLElement>, groupId?: string) => {
    // Block when read-only
    if (!canEditCriteria) return
    setAddMenuAnchor(event.currentTarget)
    setAddMenuGroupId(groupId)
  }

  // Handle add menu close
  const handleAddMenuClose = () => {
    setAddMenuAnchor(null)
    setAddMenuGroupId(undefined)
  }

  // Handle add criteria (logicGroupId is required)
  const handleAddCriteria = (conditionType: WorkflowConditionType) => {
    // Block when read-only
    if (!canEditCriteria) return
    if (!addMenuGroupId) {
      console.error('[CriteriaPanel] Cannot add criteria without a logic group ID')
      return
    }
    addCriteriaItem(conditionType, addMenuGroupId)
    handleAddMenuClose()
  }

  // Handle add logic group
  const handleAddLogicGroup = () => {
    // Block when read-only
    if (!canEditCriteria) return
    addCriteriaLogicGroup('AND')
  }

  // Render a single criteria item
  const renderCriteriaItem = (item: CriteriaItem) => {
    const isExpanded = expandedItems.has(item.id)
    const typeLabel = getConditionTypeLabel(item.conditionType)
    const typeIcon = getCriteriaTypeIcon(item.conditionType)

    return (
      <Paper
        key={item.id}
        variant="outlined"
        sx={{ mb: 1, overflow: 'hidden' }}
      >
        {/* Item Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1.5,
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' },
          }}
          onClick={() => toggleItemExpanded(item.id)}
        >
          <Typography sx={{ fontSize: '1.2rem' }}>{typeIcon}</Typography>
          <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
            {typeLabel}
          </Typography>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              removeCriteriaItem(item.id)
            }}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>

        {/* Item Config */}
        <Collapse in={isExpanded}>
          <Box sx={{ p: 2, pt: 0, borderTop: '1px solid', borderColor: 'divider' }}>
            <ConditionConfigPanel
              conditionType={item.conditionType}
              config={item.config}
              onChange={(config) => updateCriteriaItem(item.id, config)}
              measurementUnit={measurementUnit}
              adventureId={adventureId}
            />
          </Box>
        </Collapse>
      </Paper>
    )
  }

  // Render a logic group with its items
  const renderLogicGroup = (group: CriteriaLogicGroup, index: number) => {
    const groupItems = getItemsForGroup(group.id)
    const operator = getOperatorDisplay(group.isAnd)

    return (
      <Paper
        key={group.id}
        variant="outlined"
        sx={{ mb: 2, p: 2, bgcolor: 'background.default' }}
      >
        <LogicGroupHeader
          operator={operator}
          onOperatorChange={(newOperator) => updateCriteriaLogicGroup(group.id, newOperator)}
          onDelete={() => removeCriteriaLogicGroup(group.id)}
          canDelete={logicGroups.length > 1}
          showOrIndicator={index > 0}
        />

        {/* Group items */}
        <Box sx={{ mt: 2 }}>
          {groupItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 1 }}>
              No criteria in this group
            </Typography>
          ) : (
            groupItems.map(renderCriteriaItem)
          )}
        </Box>

        {/* Add to group button */}
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={(e) => handleAddMenuOpen(e, group.id)}
          sx={{ mt: 1 }}
        >
          Add Criteria to Group
        </Button>
      </Paper>
    )
  }

  return (
    <Card>
      <CardHeader
        avatar={icon}
        title={title}
        subheader={description}
        action={headerAction}
      />
      <CardContent>
        {/* Loading state */}
        {(isLoading || isCreatingInitialGroup) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {/* Published Read-Only Warning */}
        {isPublished && !canEditCriteria && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Criteria are read-only because this adventure is published. Only system moderators can modify criteria.
          </Alert>
        )}

        {/* GeoCache limited criteria notice */}
        {isGeoCache && (
          <Alert severity="info" sx={{ mb: 2 }}>
            GeoCaches have limited criteria options: Time/Schedule, GeoFence, Adventure/Collection Completion, and Day/Night only.
          </Alert>
        )}

        {/* Category-restricted criteria notice */}
        {!isGeoCache && allowedCriteriaFromCategory && allowedCriteriaFromCategory.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Available criteria options are limited by the {categorySettings?.displayName || 'category'} configuration.
          </Alert>
        )}

        {/* Info about how criteria work */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>How criteria work:</strong> Items within a group are combined using the group's operator (AND/OR).
            If you have multiple groups, <strong>any group passing</strong> means the overall criteria passes (groups are OR'd together).
          </Typography>
        </Alert>

        {/* Logic groups */}
        {logicGroups
          .sort((a, b) => a.orderIndex - b.orderIndex)
          .map(renderLogicGroup)}

        {/* Action buttons - hidden when read-only */}
        {canEditCriteria && (
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddLogicGroup}
            >
              Add Logic Group
            </Button>
          </Box>
        )}

        {/* Add criteria menu */}
        <Menu
          anchorEl={addMenuAnchor}
          open={Boolean(addMenuAnchor)}
          onClose={handleAddMenuClose}
          PaperProps={{
            sx: { maxHeight: 400, width: 280 },
          }}
        >
          {CRITERIA_CATEGORIES
            .filter((category) => category.types.some((t) => availableCriteriaTypes.includes(t)))
            .map((category, categoryIndex) => (
              <Box key={category.name}>
                {categoryIndex > 0 && <Divider />}
                <MenuItem disabled sx={{ opacity: 1 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <Typography>{category.icon}</Typography>
                  </ListItemIcon>
                  <ListItemText
                    primary={category.name}
                    primaryTypographyProps={{ fontWeight: 'bold', variant: 'body2' }}
                  />
                </MenuItem>
                {category.types
                  .filter((t) => availableCriteriaTypes.includes(t))
                  .map((conditionType) => (
                    <MenuItem
                      key={conditionType}
                      onClick={() => handleAddCriteria(conditionType)}
                      sx={{ pl: 4 }}
                    >
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <Typography>{getCriteriaTypeIcon(conditionType)}</Typography>
                      </ListItemIcon>
                      <ListItemText
                        primary={getConditionTypeLabel(conditionType)}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                      <Chip
                        label={isSubscriptionRequired(conditionType) ? 'Pro' : 'Free'}
                        size="small"
                        color={isSubscriptionRequired(conditionType) ? 'warning' : 'success'}
                        sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                      />
                    </MenuItem>
                  ))}
              </Box>
            ))}
        </Menu>

      </CardContent>
    </Card>
  )
}
